import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Briefcase, MapPin } from "lucide-react";

import { registerBusiness, api } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

const BRANCHES = [
  "Advocaat",
  "Boekhouder",
  "Verzekeringsmakelaar",
  "Energieadviseur",
  "Vastgoedmakelaar",
  "Notaris",
  "Hypotheekadviseur",
  "Kredietbemiddelaar",
  "Andere",
];
const REGIONS = [
  "Antwerpen",
  "Limburg",
  "Oost-Vlaanderen",
  "Vlaams-Brabant",
  "West-Vlaanderen",
  "Brussel",
  "Henegouwen",
  "Luik",
  "Luxemburg",
  "Namen",
  "Waals-Brabant",
  "Nederland - Noord",
  "Nederland - Midden",
  "Nederland - Zuid",
];

export default function VoorBedrijvenAanmelden() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState({
    business_name: "",
    contact_name: "",
    email: "",
    phone: "",
    vat_number: "",
    website: "",
    description: "",
  });
  const [branches, setBranches] = useState([]);
  const [regions, setRegions] = useState([]);
  const [accept, setAccept] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Google Places autocomplete
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const debounceRef = useRef(null);

  function setField(k, v) {
    setForm((s) => ({ ...s, [k]: v }));
  }

  function onBusinessNameChange(value) {
    setField("business_name", value);
    setSelectedPlace(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.length < 3) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get("/places/autocomplete", { params: { q: value } });
        setSuggestions(r.data.suggestions || []);
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      }
    }, 300);
  }

  async function pickSuggestion(s) {
    setField("business_name", s.main_text);
    setShowSuggestions(false);
    setSelectedPlace(s);
    // Fetch details om website/phone te prefillen
    try {
      const r = await api.get(`/places/details/${s.place_id}`);
      const d = r.data || {};
      setForm((prev) => ({
        ...prev,
        business_name: d.name || prev.business_name,
        website: prev.website || d.website || "",
        phone: prev.phone || d.phone || "",
      }));
    } catch {
      // niet kritisch
    }
  }

  function toggle(list, setList, value) {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  }

  async function submit(e) {
    e.preventDefault();
    if (branches.length === 0) {
      toast({ title: "Selecteer minstens 1 branche", variant: "destructive" });
      return;
    }
    if (regions.length === 0) {
      toast({ title: "Selecteer minstens 1 regio", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await registerBusiness({ ...form, branches, regions, accept_terms: accept });
      navigate("/voor-bedrijven/bedankt");
    } catch (err) {
      toast({
        title: "Aanmelding mislukt",
        description: err?.response?.data?.detail || "Probeer opnieuw of mail ons.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="py-12 bg-secondary min-h-screen" data-testid="b2b-signup-page">
      <div className="max-w-4xl mx-auto px-6">
        <Link to="/voor-bedrijven" className="text-sm text-ink-soft hover:text-ink">← Terug naar B2B-info</Link>

        <div className="flex items-center gap-3 mt-6 mb-8">
          <div className="h-12 w-12 rounded-xl bg-[#264653] text-white flex items-center justify-center">
            <Briefcase className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-ink-soft">Aanmelden</p>
            <h1 className="font-display text-3xl font-bold text-ink tracking-tight">
              Word CheckExpert partner
            </h1>
          </div>
        </div>

        <form onSubmit={submit} className="rounded-3xl bg-white border border-border p-8 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="business_name">Bedrijfsnaam *</Label>
              <div className="relative">
                <Input
                  id="business_name"
                  required
                  value={form.business_name}
                  onChange={(e) => onBusinessNameChange(e.target.value)}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  placeholder="Typ je bedrijfsnaam — wij vinden je in Google"
                  data-testid="b2b-business-name"
                  autoComplete="off"
                />
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute z-30 left-0 right-0 mt-1 rounded-xl border border-border bg-white shadow-lg max-h-72 overflow-auto" data-testid="b2b-suggestions">
                    {suggestions.map((s, idx) => (
                      <button
                        key={s.place_id}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                        className="w-full text-left px-4 py-2 hover:bg-secondary border-b border-border last:border-0 flex items-start gap-2"
                        data-testid={`b2b-autocomplete-suggestion-${idx}`}
                        role="option"
                      >
                        <MapPin className="h-4 w-4 text-[#002FA7] mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-ink truncate">{s.main_text}</div>
                          <div className="text-xs text-ink-soft truncate">{s.secondary_text}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {selectedPlace && (
                <p className="text-xs text-emerald-700 mt-1.5 flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Gevonden in Google — we verrijken je profiel automatisch met reviews en adres.
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="contact_name">Contactpersoon *</Label>
              <Input id="contact_name" required value={form.contact_name} onChange={(e) => setField("contact_name", e.target.value)} data-testid="b2b-contact-name" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="email">E-mail *</Label>
              <Input id="email" type="email" required value={form.email} onChange={(e) => setField("email", e.target.value)} data-testid="b2b-email" />
            </div>
            <div>
              <Label htmlFor="phone">Telefoon</Label>
              <Input id="phone" value={form.phone} onChange={(e) => setField("phone", e.target.value)} data-testid="b2b-phone" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="vat_number">BTW-nummer</Label>
              <Input id="vat_number" placeholder="BE0123456789" value={form.vat_number} onChange={(e) => setField("vat_number", e.target.value)} data-testid="b2b-vat" />
            </div>
            <div>
              <Label htmlFor="website">Website</Label>
              <Input id="website" placeholder="https://" value={form.website} onChange={(e) => setField("website", e.target.value)} data-testid="b2b-website" />
            </div>
          </div>

          <div>
            <Label htmlFor="description">Beschrijving van je expertise *</Label>
            <Textarea
              id="description" required minLength={10} rows={5}
              value={form.description} onChange={(e) => setField("description", e.target.value)}
              placeholder="Wat doe je, voor wie? Wat is je specialiteit? Wat onderscheidt je?"
              data-testid="b2b-description"
            />
          </div>

          <div>
            <Label>Branches *</Label>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="b2b-branches">
              {BRANCHES.map((b) => (
                <button
                  type="button"
                  key={b}
                  onClick={() => toggle(branches, setBranches, b)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    branches.includes(b)
                      ? "bg-[#264653] text-white"
                      : "bg-white border border-border text-ink-soft hover:border-ink-soft"
                  }`}
                >
                  {b}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>Regio's *</Label>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="b2b-regions">
              {REGIONS.map((r) => (
                <button
                  type="button"
                  key={r}
                  onClick={() => toggle(regions, setRegions, r)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    regions.includes(r)
                      ? "bg-[#264653] text-white"
                      : "bg-white border border-border text-ink-soft hover:border-ink-soft"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-start gap-3 pt-2">
            <Checkbox id="accept" checked={accept} onCheckedChange={(v) => setAccept(!!v)} data-testid="b2b-accept" />
            <label htmlFor="accept" className="text-xs text-ink-soft leading-relaxed">
              Ik ga akkoord met de <Link to="/algemene-voorwaarden" className="text-[#002FA7] font-semibold">algemene voorwaarden</Link> en het maandelijks tarief van €29,00 excl. BTW. Eerste maand is gratis tot de admin je profiel valideert.
            </label>
          </div>

          <button
            type="submit"
            disabled={submitting || !accept}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#264653] hover:bg-[#1F3A45] text-white font-semibold disabled:opacity-50"
            data-testid="b2b-submit"
          >
            {submitting ? "Versturen…" : "Verstuur aanmelding"} <ArrowRight className="h-4 w-4" />
          </button>

          <p className="text-xs text-ink-subtle pt-2">
            Aanmeldingen worden manueel gevalideerd door onze redactie binnen 2 werkdagen. We checken op kwalificaties en relevantie.
          </p>
        </form>
      </div>
    </div>
  );
}
